import { useLocalStorage } from "@mantine/hooks";
import {
	type PointerEventHandler,
	type ReactNode,
	createContext,
	useCallback,
	useEffect,
	useState,
} from "react";
import { useLocation } from "react-router-dom";

export interface HasSidebarContextType {
	hasSidebar: true;
	isDrawerOpen: boolean;
	isDesktop: boolean;
	toggleDrawer: PointerEventHandler<HTMLButtonElement>;
	setDrawerOpen: (open: boolean) => void;
}

export interface NoSidebarContextType {
	hasSidebar: false;
	isDrawerOpen: boolean;
	isDesktop: boolean;
	navigate: (path: string) => void;
	toggleDrawer: PointerEventHandler<HTMLButtonElement>;
	setDrawerOpen: (open: boolean) => void;
}

export const SidebarDrawerContext = createContext<
	HasSidebarContextType | NoSidebarContextType
>({
	hasSidebar: false,
	isDrawerOpen: false,
	isDesktop: false,
	navigate: () => {},
	toggleDrawer: () => {},
	setDrawerOpen: () => {},
} satisfies NoSidebarContextType);

SidebarDrawerContext.displayName = "SidebarDrawerContext";

interface MenuStateProviderProps {
	children: ReactNode;
	isDesktop: boolean;
}

export function MenuStateProvider(props: MenuStateProviderProps) {
	const { children, isDesktop } = props;

	const location = useLocation();

	// Desktop state persisted in localStorage
	const [desktopDrawerOpen, setDesktopDrawerOpen] = useLocalStorage({
		key: "sidebar-drawer-desktop",
		defaultValue: true,
	});

	// Mobile state using regular state, always starts closed
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

	// Use the appropriate state based on device type
	const isDrawerOpen = isDesktop ? desktopDrawerOpen : mobileDrawerOpen;

	// Close mobile drawer on route changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: should close the drawer when the route changes
	useEffect(() => {
		if (!isDesktop && mobileDrawerOpen) {
			setMobileDrawerOpen(false);
		}
	}, [location.pathname]);

	const toggleDrawer = useCallback<PointerEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation();
			if (isDesktop) {
				setDesktopDrawerOpen(!desktopDrawerOpen);
			} else {
				setMobileDrawerOpen(!mobileDrawerOpen);
			}
		},
		[isDesktop, desktopDrawerOpen, mobileDrawerOpen, setDesktopDrawerOpen],
	);

	const setDrawerOpen = useCallback(
		(open: boolean) => {
			if (isDesktop) {
				setDesktopDrawerOpen(open);
			} else {
				setMobileDrawerOpen(open);
			}
		},
		[isDesktop, setDesktopDrawerOpen],
	);

	const value = {
		isDrawerOpen,
		toggleDrawer,
		setDrawerOpen,
		hasSidebar: true,
		isDesktop,
	} satisfies HasSidebarContextType;

	return (
		<SidebarDrawerContext.Provider value={value}>
			{children}
		</SidebarDrawerContext.Provider>
	);
}
